/**
 * @fileoverview Tipos para el sistema modular de features
 * @module types/features
 */

import { Timestamp } from 'firebase/firestore';

/**
 * Nombres de todas las features disponibles en el sistema
 */
export type FeatureName = 
  | 'advancedTasks'
  | 'advancedActivities'
  | 'dashboardMode'
  | 'templateLibrary'
  | 'calendar'
  | 'groupGoals'
  | 'advancedRecipes'
  | 'mealPlanCalendar'
  | 'shoppingList'
  | 'gritiAI';

/**
 * Estado de una feature individual
 */
export interface FeatureState {
  /** Si el usuario puede usar esta feature (cumplió métricas o desbloqueó manual) */
  unlocked: boolean;
  
  /** Si el usuario quiere verla en su UI (toggle en Personalización) */
  enabled: boolean;
  
  /** Cuándo se desbloqueó */
  unlockedAt: Timestamp | null;
  
  /** Cómo se desbloqueó */
  unlockedBy: 'auto' | 'manual' | null;
  
  /** Si ya se mostró la notificación de desbloqueo */
  notificationShown: boolean;
}

/**
 * Colección completa de features de una familia
 */
export interface FamilyFeatures {
  advancedTasks: FeatureState;
  advancedActivities: FeatureState;
  dashboardMode: FeatureState;
  templateLibrary: FeatureState;
  calendar: FeatureState;
  groupGoals: FeatureState;
  advancedRecipes: FeatureState;
  mealPlanCalendar: FeatureState;
  shoppingList: FeatureState;
  gritiAI: FeatureState;
}

/**
 * Métricas calculadas de uso de una familia
 */
export interface FeatureMetrics {
  /** Número de tareas creadas */
  tasksCreated: number;
  
  /** Número de tareas completadas (histórico) */
  tasksCompleted: number;
  
  /** Número de actividades creadas */
  activitiesCreated: number;
  
  /** Número de recetas creadas */
  recipesCreated: number;
  
  /** Días que ha usado recetas */
  recipeDaysUsed: number;
  
  /** Días desde que se creó la familia */
  daysActive: number;
  
  /** Número de integrantes */
  membersCount: number;
  
  /** Total de tareas+actividades completadas */
  totalCompletions: number;
  
  /** Fecha de primera actividad */
  firstActivityDate: Timestamp | null;
  
  /** Si ha explorado 3+ secciones diferentes */
  hasExploredSections: boolean;
}

/**
 * Metadata de una feature para UI
 */
export interface FeatureInfo {
  name: FeatureName;
  icon: string;
  title: string;
  description: string;
  shortDescription: string;
  category: 'organization' | 'tasks' | 'cooking' | 'ai';
  includes: string[];
  dependency?: FeatureName | null;
  dependencyMessage?: string;
}

/**
 * Estado completo del hook useFeatureUnlock
 */
export interface FeatureUnlockState {
  features: FamilyFeatures | null;
  metrics: FeatureMetrics | null;
  loading: boolean;
}

/**
 * Retorno del hook useFeatureUnlock
 */
export interface UseFeatureUnlockReturn {
  features: FamilyFeatures;
  metrics: FeatureMetrics;
  loading: boolean;
  unlockFeature: (featureName: FeatureName) => Promise<void>;
  toggleFeature: (featureName: FeatureName) => Promise<void>;
  isFeatureEnabled: (featureName: FeatureName) => boolean;
  isFeatureUnlocked: (featureName: FeatureName) => boolean;
}

/**
 * Feature por defecto (bloqueado y desactivado)
 */
export const DEFAULT_FEATURE_STATE: FeatureState = {
  unlocked: false,
  enabled: false,
  unlockedAt: null,
  unlockedBy: null,
  notificationShown: false
};

/**
 * Métricas por defecto (ceros)
 */
export const DEFAULT_METRICS: FeatureMetrics = {
  tasksCreated: 0,
  tasksCompleted: 0,
  activitiesCreated: 0,
  recipesCreated: 0,
  recipeDaysUsed: 0,
  daysActive: 0,
  membersCount: 0,
  totalCompletions: 0,
  firstActivityDate: null,
  hasExploredSections: false
};

/**
 * Features por defecto (todas bloqueadas)
 */
export const DEFAULT_FAMILY_FEATURES: FamilyFeatures = {
  advancedTasks: { ...DEFAULT_FEATURE_STATE },
  advancedActivities: { ...DEFAULT_FEATURE_STATE },
  dashboardMode: { ...DEFAULT_FEATURE_STATE, unlocked: true, enabled: false }, // Desbloqueado por defecto, el usuario lo activa
  templateLibrary: { ...DEFAULT_FEATURE_STATE },
  calendar: { ...DEFAULT_FEATURE_STATE },
  groupGoals: { ...DEFAULT_FEATURE_STATE },
  advancedRecipes: { ...DEFAULT_FEATURE_STATE },
  mealPlanCalendar: { ...DEFAULT_FEATURE_STATE },
  shoppingList: { ...DEFAULT_FEATURE_STATE },
  gritiAI: { ...DEFAULT_FEATURE_STATE }
};
